// index reducer, one reducer to rule them all

import { combineReducers } from 'redux';
import storageSession from 'redux-persist/lib/storage/session';
import { persistReducer } from 'redux-persist';

import app from './app.reducer';
import rewardReducer from './rewards';
import orderingReducer from './ordering';
import commonReducer from './common';
import authenticationReducer from './authentication';

// FUDR-13759: nested persist config for the 'app' slice.
// Blacklist large API-fetched keys to keep sessionStorage lean; the keys listed here
// are re-fetched on every page load and add megabytes of payload with zero cold-start
// benefit. Retained keys: cart, selectedTable, selectedAddress, signIn, signUp,
// progressiveWebApp, persistorMarkedForPurge, userConsent, parentRoute.
const appPersistConfig = {
  // Use 'app_slice' (not 'app') to avoid collision with the pre-FUDR-13759 root
  // persist key 'persist:app' which stored the entire root state. A different key
  // ensures a clean start on first deploy without accidentally reading stale root-
  // level data into the app slice.
  key: 'app_slice',
  storage: storageSession,
  blacklist: [
    // Menu and restaurant data — fully re-fetched on every menu page load
    'menu',
    'restaurant',
    'restaurantGroup',
    'restaurantList',
    'masterData',
    'serviceTypes',
    'disabledCategories',
    'recommendedSections',
    'disabledMenuItems',
    'menuItemCustomizations',
    'menuItemVariations',
    // Cart view — server-derived from app.cart; re-fetched when cart opens
    'viewCart',
    'cartUpsellItems',
    // Orders
    'orderStatus',
    'orderedItems',
    'guestOrders',
    'previousOrders',
    'orderCount',
    // Offers
    'restaurantOffers',
    'availableOffers',
    // Location / city
    'availableCities',
    'localities',
    'verifiedLocalities',
    // Deals / cash cards
    'brandDeals',
    'brandDeal',
    'guestCashCards',
    'guestCashCardDetails',
    'guestCashCardTransactions',
    'guestCashCardRechargeDetails',
    'restaurantCashCard',
    // Guest history
    'visitedRestaurants',
    // Feedback
    'guestFeedback',
    'brandFeedbackSetup',
    'orderFeedbackSetup',
    'orderFeedbackById',
    // Misc API-fetched
    'searchResult',
    'guestData',
    'whitelabelInfo',
    'outletDetails',
    'requestedAssistancesTimeStampMap',
    'contextualHome',
    'ajaxCallsInProgress',
    'apiError',
    'customizationLoader',
  ],
};

// FUDR-13759 regression fix: common.themeData and common.resources were implicitly
// persisted under the old no-op blacklist. The FUDR-13759 whitelist change removed
// 'common' entirely, which means useTheme() never fires until the brand API responds —
// causing isThemeApplied to be false on every cold start (blank ordering/rewards screens
// until the API round-trip completes).
//
// Fix: persist only the two small config keys that useTheme() needs on cold start:
//   • themeData  — CSS custom-property values (colours, fonts, sizes) — ~2 KB
//   • resources  — brand copy, string overrides, feature flags — ~5 KB
// Root re-applies window.__FUDR_BOOT__ AFTER rehydrate so boot replaces these
// (containers/index.js + runWhenPersistorBootstrapped).
// All large API-fetched keys (masterData, outletDetails, contextualHome, etc.) are NOT
// persisted (they are absent from the whitelist).
const commonPersistConfig = {
  key: 'common_slice',
  storage: storageSession,
  whitelist: ['themeData', 'resources', 'communityContext'],
};

// Persist cart/session-relevant ordering keys; large menu/outlet payloads are re-fetched.
// dealOffer is small and needed for BOGO/free-item offer pages on hard refresh.
const orderingPersistConfig = {
  key: 'ordering_slice',
  storage: storageSession,
  whitelist: [
    'orderedItems',
    'viewCart',
    'selectedTable',
    'selectedAddress',
    'draftSelectedAddress',
    'previousSelectedTable',
    'dealOffer',
    'restaurant',
  ],
};

// Persist logged-in rewards guest profile across refresh; all other rewards keys are API-fetched.
const rewardsPersistConfig = {
  key: 'rewards_slice',
  storage: storageSession,
  whitelist: ['rewardGuestDetails'],
};

const rootReducer = combineReducers({
  app: persistReducer(appPersistConfig, app),
  rewards: persistReducer(rewardsPersistConfig, rewardReducer),
  ordering: persistReducer(orderingPersistConfig, orderingReducer),
  common: persistReducer(commonPersistConfig, commonReducer),
  authentication: authenticationReducer,
});

export default rootReducer;
